import { useEffect, useState } from "react";
import { usePortalScope } from "@/analytics/PortalScope";
import { Button, Dialog, TextInput } from "@/components/ui";
import { useAuthedApi } from "@/api/useAuthedApi";
import { RestError } from "@/api/restClient";
import { humanRole } from "./roleLabel";

interface Props {
  open: boolean;
  onClose: () => void;
  onAdded: () => void;
}

export function AddMemberDialog({ open, onClose, onAdded }: Props) {
  const { companies, roles: rolesApi } = useAuthedApi();
  // Opened only from the Members tab, so its portal names that path (D7).
  const scope = usePortalScope("members-add_member_dialog")["data-ph-scope"];
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("member");
  const [roleOptions, setRoleOptions] = useState<string[]>(["member", "admin"]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    void (async () => {
      try {
        const res = await rolesApi.list();
        const names = [...new Set(res.roles)].sort((a, b) =>
          a.localeCompare(b),
        );
        if (!cancelled && names.length > 0) {
          setRoleOptions(names);
          setRole((current) => (names.includes(current) ? current : names[0]));
        }
      } catch (e) {
        if (!cancelled && !(e instanceof RestError && e.status === 403)) {
          setRoleOptions(["member", "admin"]);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [open, rolesApi]);

  async function handleAdd() {
    if (!email.trim()) return;
    setError(null);
    setSuccess(null);
    setBusy(true);
    try {
      const res = await companies.addMember(email.trim(), role) as {
        email: string;
        provisioned: boolean;
        invite_email_sent?: boolean;
      };
      if (res.provisioned && res.invite_email_sent) {
        setSuccess(`Invite email sent to ${res.email} — they can click the link to accept and join.`);
      } else if (res.provisioned) {
        setSuccess(`Added ${res.email} — they can log in to access the organization.`);
      } else {
        setSuccess(`Added ${res.email} to the organization.`);
      }
      setEmail("");
      onAdded();
    } catch (e) {
      setError((e as Error).message || "Could not add member");
    } finally {
      setBusy(false);
    }
  }

  function handleClose() {
    if (busy) return;
    setEmail("");
    setError(null);
    setSuccess(null);
    onClose();
  }

  return (
    <Dialog open={open} onClose={handleClose} title="Add member" size="sm" scope={scope}>
      <div className="px-6 py-5 space-y-4">
        <div className="space-y-1.5">
          <label htmlFor="member-email" className="text-[13px] font-medium text-ink">
            Email address
          </label>
          <TextInput
            id="member-email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="colleague@company.com"
            autoFocus
            onKeyDown={(e) => { if (e.key === "Enter" && email.trim()) handleAdd(); }}
          />
        </div>
        <div className="space-y-1.5">
          <label htmlFor="member-role" className="text-[13px] font-medium text-ink">
            Role
          </label>
          <select
            data-ph="select"
            id="member-role"
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="w-full rounded-[10px] bg-surface border border-edge px-3 py-2 text-sm text-ink focus:outline-none focus:border-accent focus:ring-2 focus:ring-accent/25"
          >
            {roleOptions.map((r) => (
              <option key={r} value={r}>
                {humanRole(r)}
              </option>
            ))}
          </select>
        </div>
        {error && <p className="text-sm text-danger">{error}</p>}
        {success && <p className="text-sm text-emerald-600">{success}</p>}
        <div className="flex justify-end gap-2 pt-2">
          <Button data-ph="cancel" variant="secondary" size="sm" onClick={handleClose} disabled={busy}>
            Close
          </Button>
          <Button data-ph="submit" size="sm" onClick={handleAdd} disabled={busy || !email.trim()}>
            {busy ? "Adding…" : "Add member"}
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
